import { useCallback, useEffect, useState } from "react";
import { Trophy, Medal, Award, Timer, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getMetricasTempoResposta } from "@/lib/api";

const MEDAL = [
  { icon: Trophy, color: "text-amber-400", bg: "bg-amber-400/15", border: "border-amber-400/40", label: "Ouro" },
  { icon: Medal, color: "text-slate-300", bg: "bg-slate-300/15", border: "border-slate-300/40", label: "Prata" },
  { icon: Award, color: "text-orange-500", bg: "bg-orange-500/15", border: "border-orange-500/40", label: "Bronze" },
];

// Pódio dos 3 vendedores com menor tempo médio de resposta no mês corrente.
// Mostrado na Visão Geral pra virar métrica cultural.
export default function TopVendedoresSLACard() {
  const [rows, setRows] = useState([]);
  const [sla, setSla] = useState(30);
  const [loading, setLoading] = useState(true);
  const mesAtual = new Date().toISOString().slice(0, 7);
  const mesLabel = new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMetricasTempoResposta(mesAtual);
      setSla(r?.sla_minutos || 30);
      setRows(Array.isArray(r?.vendedores) ? r.vendedores : []);
    } catch (e) {
      console.error("Erro ao carregar top SLA:", e);
    } finally { setLoading(false); }
  }, [mesAtual]);

  useEffect(() => {
    load();
    const onValid = () => load();
    window.addEventListener("onboarding:validated", onValid);
    return () => window.removeEventListener("onboarding:validated", onValid);
  }, [load]);

  const top3 = rows.slice(0, 3);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="top-vendedores-sla-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Timer className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Resposta mais rápida ao cliente</h3>
        </div>
        <Badge variant="outline" className="text-[10px] capitalize">{mesLabel} · SLA {sla} min</Badge>
      </div>

      {loading ? (
        <div className="text-center py-6 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
        </div>
      ) : top3.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground">
          Nenhuma resposta a formulário registrada neste mês ainda.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {top3.map((r, idx) => {
            const m = MEDAL[idx];
            const Icon = m.icon;
            const dentroSla = r.tempo_medio_min <= sla;
            return (
              <div
                key={r.vendedor}
                data-testid={`top-sla-${idx + 1}`}
                className={`rounded-lg border-2 p-4 ${m.border} ${idx === 0 ? "bg-gradient-to-br from-amber-400/10 to-transparent" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-lg grid place-items-center ${m.bg}`}>
                    <Icon className={`w-5 h-5 ${m.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label} · #{idx + 1}</div>
                    <div className="font-heading font-semibold truncate text-sm">{r.vendedor}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className={`font-heading text-3xl font-extrabold tabular-nums ${dentroSla ? "text-emerald-500" : "text-amber-500"}`}>
                    {r.tempo_medio_min}
                  </span>
                  <span className="text-xs text-muted-foreground">min</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">
                  {r.total_respostas} resposta{r.total_respostas !== 1 ? "s" : ""} · {r.sla_pct}% dentro do SLA
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
